// src/constants/themePresets.ts
// The palette is admin-editable: these are the shipped defaults and the curated
// presets offered in the Appearance tab. Values are written to Firestore
// (settings/theme) and injected as CSS variables at runtime — see useThemeTokens.

export interface ThemeMode {
  primary: string;
  primaryDark: string;
  primaryLight: string;
  primaryInk: string;
  secondary: string;
  success: string;
  warning: string;
  error: string;
  bgPrimary: string;
  bgSecondary: string;
  bgTertiary: string;
  bgHover: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  border: string;
}

export interface ThemeTokens {
  /** id of the preset this was based on ("custom" once edited) */
  preset: string;
  radius: number;
  dark: ThemeMode;
  light: ThemeMode;
  /** id from FONT_SETS — controls display/body/mono typefaces */
  fontSet?: string;
}

/** Typeface pairings. `google` is the Google Fonts family query appended to the
 *  stylesheet URL; system stacks use an empty string. */
export interface FontSet {
  id: string;
  name: string;
  note: string;
  display: string;
  body: string;
  mono: string;
  google: string;
}

export const FONT_SETS: FontSet[] = [
  {
    id: "ember",
    name: "Fraunces + Instrument",
    note: "Editorial serif headlines — the house style",
    display: '"Fraunces", Georgia, serif',
    body: '"Instrument Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: '"JetBrains Mono", Consolas, monospace',
    google:
      "Fraunces:opsz,wght@9..144,500;9..144,600&family=Instrument+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600",
  },
  {
    id: "modern",
    name: "Space Grotesk + Inter",
    note: "Geometric and technical",
    display: '"Space Grotesk", sans-serif',
    body: '"Inter", -apple-system, sans-serif',
    mono: '"JetBrains Mono", Consolas, monospace',
    google:
      "Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600",
  },
  {
    id: "classic",
    name: "Playfair + Source Sans",
    note: "High-contrast, luxury feel",
    display: '"Playfair Display", Georgia, serif',
    body: '"Source Sans 3", -apple-system, sans-serif',
    mono: '"IBM Plex Mono", Consolas, monospace',
    google:
      "Playfair+Display:wght@500;600;700&family=Source+Sans+3:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600",
  },
  {
    id: "brutal",
    name: "Archivo + IBM Plex",
    note: "Bold, industrial, high impact",
    display: '"Archivo Black", sans-serif',
    body: '"IBM Plex Sans", -apple-system, sans-serif',
    mono: '"IBM Plex Mono", Consolas, monospace',
    google:
      "Archivo+Black&family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600",
  },
  {
    id: "system",
    name: "System stack",
    note: "Zero webfonts — fastest possible load",
    display:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    body: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, Consolas, monospace',
    google: "",
  },
];

export const DEFAULT_FONT_SET = "ember";

/** CSS variable name for each token key */
export const TOKEN_VARS: Record<keyof ThemeMode, string> = {
  primary: "--primary",
  primaryDark: "--primary-dark",
  primaryLight: "--primary-light",
  primaryInk: "--primary-ink",
  secondary: "--secondary",
  success: "--success",
  warning: "--warning",
  error: "--error",
  bgPrimary: "--bg-primary",
  bgSecondary: "--bg-secondary",
  bgTertiary: "--bg-tertiary",
  bgHover: "--bg-hover",
  textPrimary: "--text-primary",
  textSecondary: "--text-secondary",
  textMuted: "--text-muted",
  border: "--border",
};

/** Grouped for the editor UI */
export const TOKEN_GROUPS: { label: string; keys: (keyof ThemeMode)[] }[] = [
  { label: "Brand", keys: ["primary", "primaryDark", "primaryLight", "primaryInk"] },
  { label: "Status", keys: ["secondary", "success", "warning", "error"] },
  { label: "Surfaces", keys: ["bgPrimary", "bgSecondary", "bgTertiary", "bgHover", "border"] },
  { label: "Text", keys: ["textPrimary", "textSecondary", "textMuted"] },
];

export const TOKEN_LABELS: Record<keyof ThemeMode, string> = {
  primary: "Accent",
  primaryDark: "Accent (pressed)",
  primaryLight: "Accent (hover)",
  primaryInk: "Text on accent",
  secondary: "Secondary",
  success: "Success",
  warning: "Warning",
  error: "Error",
  bgPrimary: "Page background",
  bgSecondary: "Card background",
  bgTertiary: "Raised surface",
  bgHover: "Hover surface",
  textPrimary: "Primary text",
  textSecondary: "Secondary text",
  textMuted: "Muted text",
  border: "Border",
};

export const EMBER_AND_INK: ThemeTokens = {
  preset: "ember",
  radius: 12,
  dark: {
    primary: "#e8a33d",
    primaryDark: "#c9861f",
    primaryLight: "#f2bc66",
    primaryInk: "#1c1204",
    secondary: "#3ec9a7",
    success: "#3ec9a7",
    warning: "#e1b554",
    error: "#e06552",
    bgPrimary: "#0c0d0f",
    bgSecondary: "#14161a",
    bgTertiary: "#1c1f24",
    bgHover: "#24282e",
    textPrimary: "#f1eee7",
    textSecondary: "#a6a399",
    textMuted: "#7a766c",
    border: "#272b32",
  },
  light: {
    primary: "#b0741a",
    primaryDark: "#8f5d12",
    primaryLight: "#d19339",
    primaryInk: "#ffffff",
    secondary: "#14907a",
    success: "#14907a",
    warning: "#a97e1c",
    error: "#c74e3c",
    bgPrimary: "#faf7f1",
    bgSecondary: "#ffffff",
    bgTertiary: "#f0ebe1",
    bgHover: "#f3eee5",
    textPrimary: "#211d16",
    textSecondary: "#5a564c",
    textMuted: "#8a8578",
    border: "#e4ddcf",
  },
};

const MINT_AND_INK: ThemeTokens = {
  preset: "mint",
  radius: 12,
  dark: {
    ...EMBER_AND_INK.dark,
    primary: "#3ed6b0",
    primaryDark: "#22ab8b",
    primaryLight: "#6ee3c6",
    primaryInk: "#04221b",
    secondary: "#e8a33d",
    warning: "#e8a33d",
    bgPrimary: "#0a0e0d",
    bgSecondary: "#121818",
    bgTertiary: "#1a2222",
    bgHover: "#222c2b",
    textPrimary: "#eaf1ef",
    border: "#23302e",
  },
  light: {
    ...EMBER_AND_INK.light,
    primary: "#0f8f74",
    primaryDark: "#0b7460",
    primaryLight: "#2eab8f",
    primaryInk: "#ffffff",
    secondary: "#b0741a",
    warning: "#a97e1c",
    bgPrimary: "#f5faf8",
    bgTertiary: "#e6f0ec",
    bgHover: "#edf5f2",
    border: "#d5e5df",
  },
};

const VIOLET_AND_INK: ThemeTokens = {
  preset: "violet",
  radius: 14,
  dark: {
    ...EMBER_AND_INK.dark,
    primary: "#a78bfa",
    primaryDark: "#8b6ceb",
    primaryLight: "#c4b1fd",
    primaryInk: "#180f33",
    secondary: "#5eead4",
    success: "#5eead4",
    bgPrimary: "#0b0a10",
    bgSecondary: "#14131c",
    bgTertiary: "#1d1b28",
    bgHover: "#262332",
    textPrimary: "#eeecf5",
    border: "#2a2738",
  },
  light: {
    ...EMBER_AND_INK.light,
    primary: "#6d47d9",
    primaryDark: "#5836b4",
    primaryLight: "#8a6bf0",
    primaryInk: "#ffffff",
    secondary: "#0f8f74",
    success: "#0f8f74",
    bgPrimary: "#faf9fd",
    bgTertiary: "#efecf7",
    bgHover: "#f4f1fa",
    border: "#e0dcee",
  },
};

const CLAY_AND_PAPER: ThemeTokens = {
  preset: "clay",
  radius: 10,
  dark: {
    ...EMBER_AND_INK.dark,
    primary: "#e2725b",
    primaryDark: "#c25742",
    primaryLight: "#ee9482",
    primaryInk: "#2a0e07",
    secondary: "#7fb069",
    success: "#7fb069",
    bgPrimary: "#100d0c",
    bgSecondary: "#191514",
    bgTertiary: "#221d1b",
    bgHover: "#2b2523",
    textPrimary: "#f2ece9",
    border: "#2e2725",
  },
  light: {
    ...EMBER_AND_INK.light,
    primary: "#b8452e",
    primaryDark: "#973723",
    primaryLight: "#d16148",
    secondary: "#4f7a3a",
    success: "#4f7a3a",
    bgPrimary: "#fbf7f4",
    bgTertiary: "#f1e9e3",
    bgHover: "#f5efea",
    border: "#e6dbd2",
  },
};

/** Build a preset by overriding only what changes from the house palette. */
const preset = (
  id: string,
  dark: Partial<ThemeMode>,
  light: Partial<ThemeMode>,
  radius = 12
): ThemeTokens => ({
  preset: id,
  radius,
  dark: { ...EMBER_AND_INK.dark, ...dark },
  light: { ...EMBER_AND_INK.light, ...light },
});

/* --- the palette this site used before the rebrand, kept as an option --- */
const GOOGLE_BLUE = preset(
  "google",
  {
    primary: "#4285f4",
    primaryDark: "#1a73e8",
    primaryLight: "#669df6",
    primaryInk: "#ffffff",
    secondary: "#34a853",
    success: "#34a853",
    warning: "#fbbc04",
    error: "#ea4335",
    bgPrimary: "#202124",
    bgSecondary: "#292a2d",
    bgTertiary: "#35363a",
    bgHover: "#3c4043",
    textPrimary: "#e8eaed",
    textSecondary: "#9aa0a6",
    textMuted: "#5f6368",
    border: "#3c4043",
  },
  {
    primary: "#1a73e8",
    primaryDark: "#1557b0",
    primaryLight: "#4285f4",
    primaryInk: "#ffffff",
    secondary: "#188038",
    success: "#188038",
    warning: "#b06000",
    error: "#c5221f",
    bgPrimary: "#ffffff",
    bgSecondary: "#f8f9fa",
    bgTertiary: "#e8eaed",
    bgHover: "#f1f3f4",
    textPrimary: "#202124",
    textSecondary: "#5f6368",
    textMuted: "#80868b",
    border: "#dadce0",
  },
  8
);

/* --- seasonal --- */
const CHRISTMAS = preset(
  "christmas",
  {
    primary: "#e5484d", primaryDark: "#c62f36", primaryLight: "#f06a6e", primaryInk: "#fff5f5",
    secondary: "#3fae6b", success: "#3fae6b", warning: "#e0b64c",
    bgPrimary: "#0b1210", bgSecondary: "#111b18", bgTertiary: "#182521", bgHover: "#1f2f2a",
    textPrimary: "#f2f6f3", textSecondary: "#a3b3ab", textMuted: "#75847d", border: "#213029",
  },
  {
    primary: "#b3242a", primaryDark: "#8f1a20", primaryLight: "#d24047", primaryInk: "#ffffff",
    secondary: "#1f7a45", success: "#1f7a45", warning: "#9a7b18",
    bgPrimary: "#fbf7f5", bgSecondary: "#ffffff", bgTertiary: "#f1e9e6", bgHover: "#f6efec",
    textPrimary: "#1b2420", textSecondary: "#54615a", textMuted: "#84908a", border: "#e2ded6",
  }
);

const HALLOWEEN = preset(
  "halloween",
  {
    primary: "#ff7518", primaryDark: "#d95c0c", primaryLight: "#ff9448", primaryInk: "#1a0d02",
    secondary: "#a468e0", success: "#7bc95f", warning: "#ffb020", error: "#e5484d",
    bgPrimary: "#0a0710", bgSecondary: "#140f1c", bgTertiary: "#1d1626", bgHover: "#271d33",
    textPrimary: "#f3eef7", textSecondary: "#a79cb3", textMuted: "#786e84", border: "#2a2036",
  },
  {
    primary: "#c1520a", primaryDark: "#9c4007", primaryLight: "#e0722a", primaryInk: "#ffffff",
    secondary: "#7038b8", success: "#4a8f2f", warning: "#a8720c",
    bgPrimary: "#faf6f2", bgSecondary: "#ffffff", bgTertiary: "#f0e8e0", bgHover: "#f5efe8",
    textPrimary: "#241a2e", textSecondary: "#5b5064", textMuted: "#8a8091", border: "#e5dcd4",
  }
);

const WINTER = preset(
  "winter",
  {
    primary: "#7cc4f0", primaryDark: "#4fa6db", primaryLight: "#a5d9f7", primaryInk: "#04121c",
    secondary: "#9fb8d4", success: "#5fc9a8", warning: "#e2cd76",
    bgPrimary: "#080d13", bgSecondary: "#0f161f", bgTertiary: "#161f2b", bgHover: "#1e2937",
    textPrimary: "#eef4fa", textSecondary: "#9fb0c1", textMuted: "#71818f", border: "#1f2a37",
  },
  {
    primary: "#2b7fb8", primaryDark: "#1f6494", primaryLight: "#4a9bd1", primaryInk: "#ffffff",
    secondary: "#4a6b8a", success: "#128a6e", warning: "#9a7a1e",
    bgPrimary: "#f5f9fc", bgSecondary: "#ffffff", bgTertiary: "#e7eff5", bgHover: "#eff5f9",
    textPrimary: "#12202c", textSecondary: "#4c5f70", textMuted: "#7e8d9a", border: "#dae5ed",
  }
);

const SUMMER = preset(
  "summer",
  {
    primary: "#ffb703", primaryDark: "#e09600", primaryLight: "#ffc933", primaryInk: "#1d1400",
    secondary: "#00b4d8", success: "#06d6a0", warning: "#fb8500", error: "#ef476f",
    bgPrimary: "#0a1014", bgSecondary: "#11191f", bgTertiary: "#18232b", bgHover: "#203038",
    textPrimary: "#f0f6f8", textSecondary: "#9fb2bb", textMuted: "#72858e", border: "#1f2d36",
  },
  {
    primary: "#e07b00", primaryDark: "#b86400", primaryLight: "#f59b1f", primaryInk: "#ffffff",
    secondary: "#0088a8", success: "#01916e", warning: "#c25f00",
    bgPrimary: "#fffaf0", bgSecondary: "#ffffff", bgTertiary: "#fdf0dc", bgHover: "#fef6e8",
    textPrimary: "#1d2328", textSecondary: "#54606a", textMuted: "#84909a", border: "#f0e3cf",
  }
);

const MIDNIGHT = preset(
  "midnight",
  {
    primary: "#6ee7d0", primaryDark: "#3ec9b0", primaryLight: "#9df3e3", primaryInk: "#03191a",
    secondary: "#8b9dff", success: "#6ee7d0", warning: "#f0c674",
    bgPrimary: "#070a12", bgSecondary: "#0d1220", bgTertiary: "#141b2c", bgHover: "#1c2438",
    textPrimary: "#eaeefb", textSecondary: "#9aa3c0", textMuted: "#6c7590", border: "#1b2338",
  },
  {
    primary: "#0e8c78", primaryDark: "#0a6f5f", primaryLight: "#2aa893", primaryInk: "#ffffff",
    secondary: "#4a5bd4", success: "#0e8c78", warning: "#9a7a1e",
    bgPrimary: "#f6f8fd", bgSecondary: "#ffffff", bgTertiary: "#e9edf8", bgHover: "#f1f4fb",
    textPrimary: "#131829", textSecondary: "#4d566e", textMuted: "#7d859b", border: "#dde3f0",
  },
  14
);

export const THEME_PRESETS: { id: string; name: string; group: string; tokens: ThemeTokens }[] = [
  { id: "ember", name: "Ember & Ink", group: "House", tokens: EMBER_AND_INK },
  { id: "mint", name: "Mint & Ink", group: "House", tokens: MINT_AND_INK },
  { id: "violet", name: "Violet & Ink", group: "House", tokens: VIOLET_AND_INK },
  { id: "clay", name: "Clay & Paper", group: "House", tokens: CLAY_AND_PAPER },
  { id: "midnight", name: "Midnight", group: "House", tokens: MIDNIGHT },
  { id: "google", name: "Classic Blue", group: "Classic", tokens: GOOGLE_BLUE },
  { id: "christmas", name: "Christmas", group: "Seasonal", tokens: CHRISTMAS },
  { id: "halloween", name: "Halloween", group: "Seasonal", tokens: HALLOWEEN },
  { id: "winter", name: "Winter", group: "Seasonal", tokens: WINTER },
  { id: "summer", name: "Summer", group: "Seasonal", tokens: SUMMER },
];

export const DEFAULT_TOKENS = EMBER_AND_INK;

/* ---------- contrast helpers (WCAG) ---------- */

const toRgb = (hex: string) => {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const luminance = (hex: string) => {
  const { r, g, b } = toRgb(hex);
  const [R, G, B] = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

/** WCAG contrast ratio between two hex colors (1–21) */
export const contrastRatio = (a: string, b: string): number => {
  try {
    const la = luminance(a);
    const lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  } catch {
    return 0;
  }
};

/** Readability checks surfaced as warnings in the editor */
export const auditMode = (m: ThemeMode): string[] => {
  const issues: string[] = [];
  const check = (fg: string, bg: string, min: number, what: string) => {
    const r = contrastRatio(fg, bg);
    if (r && r < min) issues.push(`${what}: ${r.toFixed(1)}:1 (needs ${min}:1)`);
  };
  check(m.textPrimary, m.bgPrimary, 4.5, "Primary text on page");
  check(m.textSecondary, m.bgSecondary, 4.5, "Secondary text on cards");
  check(m.primaryInk, m.primary, 4.5, "Text on accent buttons");
  check(m.primary, m.bgPrimary, 3, "Accent on page background");
  return issues;
};
